// src/features/groups/types/index.ts - FUNCIONES CORREGIDAS

import { RaRecord } from 'react-admin';

// ============================================
// MODELOS DE DATOS
// ============================================

/**
 * Interfaz principal para un Grupo
 */
export interface Group extends RaRecord {
  id: string;
  name: string;              // Nombre del grupo
  group_name?: string;       // Compatibilidad con API legacy
  description?: string;      // Descripción opcional
  hotelCount?: number;       // Número de hoteles asociados
  userCount?: number;        // Número de usuarios asociados
  isActive: boolean;         // Estado activo/inactivo
  createdAt: string;         // Fecha de creación ISO
  updatedAt: string;         // Fecha de actualización ISO
  owner_info?: string;       // Info del propietario (legacy)
}

/**
 * Datos del formulario para crear/editar grupo
 */
export interface GroupFormData {
  name: string;
  description?: string;
  isActive?: boolean;
}

/**
 * Filtros para la lista de grupos
 */
export interface GroupFilters {
  search?: string;
  isActive?: boolean;
  hasHotels?: boolean;
}

/**
 * Estadísticas de un grupo
 */
export interface GroupStats {
  totalHotels: number;
  activeHotels: number;
  totalUsers: number;
  activeUsers: number;
  totalLockers: number;
  activeAssignments: number;
}

/**
 * Respuesta de la API para lista de grupos
 */
export interface GroupListResponse {
  data: Group[];
  total: number;
  page: number;
  pageSize: number;
}

/**
 * Props para componentes del feature
 */
export interface GroupListProps {
  // Props específicas si las necesitas
}

export interface GroupCreateProps {
  onSuccess?: () => void;
  onCancel?: () => void;
}

export interface GroupEditProps {
  id?: string;
  record?: Group;
  onSuccess?: () => void;
  onCancel?: () => void;
}

export interface GroupActionsProps {
  record: Group;
  small?: boolean;
}

// ============================================
// TIPOS DE VALIDACIÓN
// ============================================

export interface GroupValidationErrors {
  name?: string;
  description?: string;
}

// ============================================
// TIPOS DE HOOKS
// ============================================

export interface UseGroupDataResult {
  groups: Group[];
  loading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
  stats?: GroupStats;
}

export interface UseGroupPermissionsResult {
  // Permisos básicos
  canManageGroups: boolean;
  canViewGroups: boolean;
  canCreateGroups: boolean;
  canEditGroups: boolean;
  canDeleteGroups: boolean;
  
  // Permisos extendidos
  canManageHotels: boolean;
  canViewStats: boolean;
  canExportData: boolean;
  canEditThisGroup: boolean;
  canDeleteThisGroup: boolean;
  
  // Permisos de negocio específicos
  canAddHotelsToGroup: (group?: Group) => boolean;
  canRemoveHotelsFromGroup: (group?: Group) => boolean;
  canTransferHotelsBetweenGroups: () => boolean;
  canViewGroupFinancials: (group?: Group) => boolean;
  canMergeGroups: () => boolean;
  canArchiveGroup: (group?: Group) => boolean;
  
  // Utilidades del sistema base
  hasRole: (...roles: Array<import('@/types/models').UserRole>) => boolean;
  isAtLeastRole: (minRole: import('@/types/models').UserRole) => boolean;
  currentRole: import('@/types/models').UserRole | null;
}

// ============================================
// UTILIDADES DE TIPO - CORREGIDAS
// ============================================

/**
 * Normaliza el grupo del formato API al formato de la aplicación
 */
export const normalizeGroup = (apiGroup: any): Group => ({
  ...apiGroup,
  id: apiGroup.id,
  name: apiGroup.name || apiGroup.group_name, // Cambiar el orden de prioridad
  description: apiGroup.description || '',
  isActive: apiGroup.isActive ?? true,
  hotelCount: apiGroup.hotelCount || 0,
  userCount: apiGroup.userCount || 0,
  createdAt: apiGroup.createdAt || new Date().toISOString(),
  updatedAt: apiGroup.updatedAt || new Date().toISOString(),
});

/**
 * Prepara los datos del grupo para enviar a la API
 * NOTA: El backend espera 'name', no 'group_name'
 */
export const prepareGroupForAPI = (group: GroupFormData): any => ({
  name: group.name?.trim(),
  description: group.description?.trim() || '',
  // NO enviar group_name, el backend espera 'name'
});

/**
 * Prepara los datos para crear un grupo
 */
export const prepareGroupForCreate = (group: GroupFormData): any => ({
  name: group.name?.trim(),
  description: group.description?.trim() || '',
});

/**
 * Prepara los datos para actualizar un grupo
 */
export const prepareGroupForUpdate = (group: GroupFormData): any => ({
  name: group.name?.trim(),
  description: group.description?.trim() || '',
});